/**
 * REF-2 — the UNIFIED Refunds drill-in.
 *
 * ONE route, ONE component, ONE test surface. The Snapshot `REFUND`, Sales `REFUNDS` and
 * EOD `TOTAL REFUNDS` KPI cards all land here; the three per-report refund drill-ins they
 * used to open are retired. Only the breadcrumb and the back destination differ, and both
 * are driven off the `origin` query param rather than off a component variant — a variant
 * is exactly what this story exists to remove.
 *
 * Everything else — date range, outlet, sales-source toggle — carries through from the
 * originating report in the query string, so the page never re-resolves a period of its
 * own and can never disagree with the KPI that opened it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Phone from '../../Phone';
import BottomNav from '../../BottomNav';
import TopBar from '../../layout/TopBar';
import DesktopLayout from '../../layout/DesktopLayout';
import { useDesktopTopBar } from '../../../context/DesktopLayoutContext';
import { T, Bone, useIsDesktop } from '../reportTokens';
import SectionError from '../SectionError';
import { getStartEndFromFilter, isTodayRange, todayStr, yesterdayStr } from '../../../services/reportsApi';
import { useStoreTimeZone } from '../../../utils/dateTimeUtils';
import { exportPeriodFor } from '../../../utils/reportPeriod';
import type { RefundLogFilterChange, RefundLogFilters } from './refundLogQuery';
import type { RefundRecord, RefundSalesSource } from './refundModel';
import { useRefundLogPeriod } from './useRefundLogPeriod';
import { useRefundReasonsPeriod } from './useRefundReasonsPeriod';
import { useRefundByStaffPeriod } from './useRefundByStaffPeriod';
import { useRefundComponentSplit } from './useRefundComponentSplit';
import {
    RefundComponentSplit,
    RefundContextStrip,
    RefundReasonsCard,
    RefundsByStaffCard,
} from './RefundSections';
import { RefundLogTable } from './RefundLogTable';
import { OrderDetailSlider } from '../components/OrderDetailSlider';
import { useBranch } from '../../../context/BranchContext';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchOrderDetailRequest, clearOrderDetail } from '../../../redux/reports/clickHouse/clickHouseActions';

/** Which report the reader came from. Drives the breadcrumb and the back destination. */
export type RefundOrigin = 'snapshot' | 'sales' | 'eod';

const ORIGIN_LABEL: Record<RefundOrigin, string> = {
    snapshot: 'Snapshot',
    sales: 'Sales',
    eod: 'EOD Summary',
};

const ORIGIN_PATH: Record<RefundOrigin, string> = {
    snapshot: '/reports/snapshot',
    sales: '/reports/sales',
    eod: '/reports/eod',
};

/**
 * The Component split card (REF-2) is hidden for now at product's request. The component and
 * its data are untouched — flip this to bring the card back.
 */
const SHOW_COMPONENT_SPLIT = true;

const isOrigin = (v: string | null): v is RefundOrigin =>
    v === 'snapshot' || v === 'sales' || v === 'eod';

/**
 * REF-7 telemetry — no new events. `maghil_reports_drill_open` gains `drill_target` and
 * `origin_report`, which is how consolidation onto one page is actually verified: three
 * origins, one target.
 */
function track(event: string, payload: Record<string, unknown>) {
    const w = window as any;
    try {
        if (typeof w.maghilTrack === 'function') w.maghilTrack(event, payload);
        else if (Array.isArray(w.dataLayer)) w.dataLayer.push({ event, ...payload });
        else if (process.env.NODE_ENV !== 'production') console.debug('[telemetry]', event, payload);
    } catch {
        /* telemetry must never break the drill */
    }
}

const trackDrillOpen = (origin: RefundOrigin) =>
    track('maghil_reports_drill_open', { drill_target: 'refunds', origin_report: origin });

/**
 * REF-7 — `maghil_reports_table_filter` gains `refund_type` as a filterable dimension; the
 * other Refund Log filters ride on the same event. The search TEXT is deliberately never
 * sent — it can hold a guest's name — only whether a search is active. Reason is now literal
 * text (from GET /refund-log-filters), not a fixed group, so it rides on the same
 * count-only convention as staff and channel rather than being joined into the payload.
 */
const trackRefundLogFilter = (f: RefundLogFilters) =>
    track('maghil_reports_table_filter', {
        table: 'refund_log',
        refund_type: f.types.length ? f.types.join(',') : 'all',
        reason_count: f.reasons.length,
        component: f.components.length ? f.components.join(',') : 'all',
        staff_count: f.staff.length,
        channel_count: f.channels.length,
        has_search: f.search.trim().length > 0,
    });

/** 'Sep 1 – Sep 9, 2026' for the context strip. */
function periodLabel(startDate: string, endDate: string): string {
    const fmt = (iso: string, withYear: boolean) => {
        const [y, m, d] = iso.split('-').map(Number);
        if (!y || !m || !d) return iso;
        const month = new Date(Date.UTC(y, m - 1, d)).toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
        return withYear ? `${month} ${d}, ${y}` : `${month} ${d}`;
    };
    if (startDate === endDate) return fmt(startDate, true);
    const sameYear = startDate.slice(0, 4) === endDate.slice(0, 4);
    return `${fmt(startDate, !sameYear)} – ${fmt(endDate, true)}`;
}

function RefundsBody() {
    const isDesktop = useIsDesktop();
    const [params] = useSearchParams();
    const storeTz = useStoreTimeZone();
    const today = useMemo(() => todayStr(storeTz), [storeTz]);
    const yesterday = useMemo(() => yesterdayStr(storeTz), [storeTz]);

    // Order Detail panel — a Refund Log row opens the order it belongs to, using the `orderId` the
    // refund-log response carries.
    const dispatch = useAppDispatch();
    const { selectedBranch: branch } = useBranch();
    const locationId = branch?.id ?? branch?.locationId ?? '';
    const [selectedRefund, setSelectedRefund] = useState<RefundRecord | null>(null);
    const orderDetailData = useAppSelector((s) => s.clickHouse.orderDetailData);
    const orderDetailLoading = useAppSelector((s) => s.clickHouse.orderDetailLoading);
    const orderDetailError = useAppSelector((s) => s.clickHouse.orderDetailError);

    // The originating report owns the period; this page only reads it back.
    const dateFilter = params.get('dateFilter') || 'This Month';
    const range = useMemo(() => {
        const from = params.get('from');
        const to = params.get('to');
        if (from && to) return { startDate: from, endDate: to };
        return getStartEndFromFilter(dateFilter, today, yesterday) ?? { startDate: today, endDate: today };
    }, [params, dateFilter, today, yesterday]);

    const exportDateRangeLabel = exportPeriodFor(dateFilter, range)?.label ?? dateFilter;
    const source = (params.get('source') as RefundSalesSource) || 'overall';
    // Shape-based, not a literal "Today" token match — a Custom range whose from/to both land
    // on today's date (store timezone) is Today too. Matches Snapshot / Sales / EOD
    // (isTodayRange, ZAR-1762): the /sales/live/v2/* endpoints these three hooks read return
    // today's rows only when startDate/endDate are OMITTED — sending today→today comes back
    // empty — so the dates are only sent for a genuinely historical range.
    const isToday = isTodayRange(range.startDate, range.endDate, today);
    const chDates = isToday ? {} : { startDate: range.startDate, endDate: range.endDate };

    // A refund posts on the day it happened, but its order can be from an earlier day: the live
    // endpoint only knows today's orders, so anything else goes through the historical lookup.
    const loadOrderDetail = useCallback((rec: RefundRecord) => {
        if (!locationId || !rec.orderId) return;
        const historical = !isToday || rec.orderDate !== today;
        dispatch(fetchOrderDetailRequest({ locationId, orderId: rec.orderId, ...(historical ? { type: 'historical' as const } : {}) }));
    }, [dispatch, locationId, isToday, today]);
    const openOrder = useCallback((rec: RefundRecord) => {
        if (!locationId || !rec.orderId) return; // an older backend without orderId: nothing to open
        // Dispatch first so the panel never paints the previously opened order while this one loads.
        loadOrderDetail(rec);
        setSelectedRefund(rec);
    }, [locationId, loadOrderDetail]);
    const closeOrder = useCallback(() => {
        setSelectedRefund(null);
        dispatch(clearOrderDetail());
    }, [dispatch]);
    // Leaving the page must not leave the last order's detail in the store for the next screen.
    useEffect(() => () => { dispatch(clearOrderDetail()); }, [dispatch]);
    const { summary, records, loading, failure, retry } = useRefundLogPeriod({ ...chDates, source });
    // REF-6 — a separate endpoint, fetched independently. If it fails while the log itself
    // succeeded, degrade gracefully to the log-derived reasons rather than blank the page.
    const reasonsQuery = useRefundReasonsPeriod({ ...chDates, source });
    const { reasons, loading: reasonsLoading, failure: reasonsFailure } = reasonsQuery;
    // REF-6 — same independent-fetch, graceful-degrade pattern for Refunds by Staff.
    const staffQuery = useRefundByStaffPeriod({ ...chDates, source });
    const { staff, loading: staffLoading, failure: staffFailure } = staffQuery;
    // Page changes keep the cards on screen (`loading` is first-load only); the pagers lock while one loads.
    const reasonsPagination = { page: reasonsQuery.page, totalPages: reasonsQuery.totalPages, onPageChange: reasonsQuery.setPage, busy: reasonsQuery.fetching };
    const staffPagination = { page: staffQuery.page, totalPages: staffQuery.totalPages, onPageChange: staffQuery.setPage, busy: staffQuery.fetching };
    const staffRankOffset = (staffQuery.page - 1) * staffQuery.pageSize;
    // REF-1 — same independent-fetch, graceful-degrade pattern for the Component Split card.
    const componentSplit = useRefundComponentSplit({ ...chDates, source });

    // Search is not reported per keystroke; its state still rides on the next filter event.
    const onLogFiltersChange = useCallback((f: RefundLogFilters, changed: RefundLogFilterChange) => {
        if (changed !== 'search') trackRefundLogFilter(f);
    }, []);

    if (loading || reasonsLoading || staffLoading || componentSplit.loading) {
        return (
            <div style={{ padding: isDesktop ? 24 : '8px 14px 20px', fontFamily: T.font, color: T.t1 }}>
                <Bone w={220} h={14} r={4} style={{ marginBottom: 16 }} />
                <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: isDesktop ? 'nowrap' : 'wrap' }}>
                    <Bone w="100%" h={180} r={12} />
                    {isDesktop && <Bone w="100%" h={180} r={12} />}
                </div>
                <Bone w="100%" h={280} r={12} />
            </div>
        );
    }

    if (failure) {
        return (
            <div style={{ padding: isDesktop ? 24 : '8px 14px 20px' }}>
                <SectionError kpiName="refund log" onRetry={retry} size="medium" minHeight={280} />
            </div>
        );
    }

    return (
        <div style={{ padding: isDesktop ? 24 : '8px 14px 20px', fontFamily: T.font, color: T.t1 }}>
            <RefundContextStrip summary={summary} periodLabel={periodLabel(range.startDate, range.endDate)} />

            {/* REF-2 — when shown, the component split goes FIRST: it is what reconciles the
                REFUND KPI the reader just tapped with the waterfall row that reads lower. */}
            {SHOW_COMPONENT_SPLIT && (
                <RefundComponentSplit
                    source={source}
                    split={componentSplit}
                    failure={componentSplit.failure}
                    onRetry={componentSplit.retry}
                />
            )}

            {isDesktop ? (
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}><RefundReasonsCard summary={summary} reasons={reasonsFailure ? undefined : reasons} pagination={reasonsPagination} /></div>
                    <div style={{ flex: 1, minWidth: 0 }}><RefundsByStaffCard summary={summary} staff={staffFailure ? undefined : staff} pagination={staffPagination} rankOffset={staffRankOffset} /></div>
                </div>
            ) : (
                <>
                    <RefundReasonsCard summary={summary} reasons={reasonsFailure ? undefined : reasons} pagination={reasonsPagination} />
                    <RefundsByStaffCard summary={summary} staff={staffFailure ? undefined : staff} pagination={staffPagination} rankOffset={staffRankOffset} />
                </>
            )}

            {/* Sort and filter live INSIDE the log and narrow it alone — the cards above
                always describe the whole period. The table now runs its own server-driven
                search/sort (searchItem/sortBy/sortAsc); `records` above stays the whole-period,
                unfiltered fetch that feeds the cards and this table's "no refunds in period" /
                Staff & Channel option universe (see RefundLogTable's own comment). */}
            <RefundLogTable
                allRecords={records}
                startDate={chDates.startDate}
                endDate={chDates.endDate}
                source={source}
                onFiltersChange={onLogFiltersChange}
                onRowClick={openOrder}
                exportDateRangeLabel={exportDateRangeLabel}
            />

            <OrderDetailSlider
                visible={!!selectedRefund}
                onClose={closeOrder}
                orderNo={selectedRefund?.orderNo}
                orderType={selectedRefund?.channel}
                loading={orderDetailLoading}
                error={orderDetailError}
                onRetry={() => selectedRefund && loadOrderDetail(selectedRefund)}
                detail={orderDetailData}
            />
        </div>
    );
}

export default function RefundsDrillPage() {
    const isDesktop = useIsDesktop();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const setTopBarProps = useDesktopTopBar();

    const originParam = params.get('origin');
    const origin: RefundOrigin = isOrigin(originParam) ? originParam : 'snapshot';

    useEffect(() => { trackDrillOpen(origin); }, [origin]);

    /**
     * REF-2 — "Back navigation returns to the originating report at its prior scroll
     * position." A history POP is what restores scroll, so a real back is preferred and the
     * origin path is only a fallback for a deep link / refresh, where there is no prior
     * position to return to.
     */
    const onBack = useCallback(() => {
        const cameFromApp = (window.history.state?.idx ?? 0) > 0;
        if (cameFromApp) navigate(-1);
        else navigate(ORIGIN_PATH[origin]);
    }, [navigate, origin]);

    // Set on BOTH layouts: the mobile TopBar reads its title and back handler from the same
    // context the desktop one does, so a single call drives the breadcrumb and both headers.
    //
    // `breadcrumbs` deliberately stops BEFORE "Refunds" — TopBar renders `title` as the
    // final bold segment itself, so including it in `breadcrumbs` too duplicated it
    // ("Snapshot › Refunds › Refunds"). `leftAddon` is this app's desktop back-button
    // convention (every other drill screen sets one); this page is always a drill, so it
    // renders unconditionally rather than behind a `drillView` check.
    useEffect(() => {
        setTopBarProps({
            breadcrumbs: `Reports › ${ORIGIN_LABEL[origin]}`,
            title: 'Refunds',
            onBack,
            leftAddon: (
                <div onClick={onBack} style={{ width: 28, height: 28, borderRadius: 6, background: T.hover, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginRight: 4 }}>
                    <span className="material-symbols-rounded" style={{ fontSize: 16, color: T.t2 }}>arrow_back</span>
                </div>
            ),
        });
    }, [setTopBarProps, origin, onBack]);

    if (isDesktop) {
        return (
            <DesktopLayout>
                <RefundsBody />
            </DesktopLayout>
        );
    }

    return (
        <Phone>
            <TopBar variant="mobile" />
            <div style={{ flex: 1, overflowY: 'auto' }}>
                <RefundsBody />
            </div>
            <BottomNav />
        </Phone>
    );
}
